import axios from 'axios';
import { useAuthStore } from '@/store/authStore';

const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// â”€â”€ Request interceptor: attach access token â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// â”€â”€ Response interceptor: handle 401 + token refresh â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token!);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && error.response?.data?.code === 'TOKEN_EXPIRED' && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = useAuthStore.getState().refreshToken;
        const { data } = await axios.post('/api/v1/auth/refresh', { refreshToken });
        const newToken = data.data.accessToken;

        useAuthStore.getState().setAccessToken(newToken);
        processQueue(null, newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (err) {
        processQueue(err);
        useAuthStore.getState().clearAuth();
        window.location.href = '/login';
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// â”€â”€ Typed API helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const authApi = {
  login: (email: string, password: string) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  forgotPassword: (email: string) => api.post('/auth/forgot-password', { email }),
  resetPassword: (email: string, otp: string, newPassword: string) => api.post('/auth/reset-password', { email, otp, newPassword }),
  updateTourVersion: (version: number) => api.patch('/auth/update-tour-version', { version }),
};

export const userApi = {
  getMe: () => api.get('/users/me'),
  getAll: () => api.get('/users'),
  getActive: () => api.get('/users/active'),
  getDirectReports: (managerId: string) => api.get(`/users/direct-reports/${managerId}`),
  create: (data: any) => api.post('/users', data),
  update: (id: string, data: any) => api.patch(`/users/${id}`, data),
  updateProfile: (data: any) => api.patch('/users/me/profile', data),
  uploadPhoto: (id: string, formData: FormData) => api.patch(`/users/${id}/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  removePhoto: (id: string) => api.delete(`/users/${id}/photo`),
  changePassword: (id: string, data: any) => api.patch(`/users/${id}/change-password`, data),
  adminResetPassword: (id: string) => api.patch(`/users/${id}/admin-reset-password`),
  deactivate: (id: string) => api.delete(`/users/${id}`),
};

export const leaveApi = {
  getAll: (params?: any) => api.get('/leaves', { params }),
  getMyLeaves: (params?: any) => api.get('/leaves', { params }),
  getBalance: (year?: number) => api.get('/leaves/balance', { params: { year } }),
  getCalendar: (params?: any) => api.get('/leaves/calendar', { params }),
  apply: (formData: FormData) => api.post('/leaves', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  validate: (data: any) => api.post('/leaves/validate', data),
  cancel: (id: string) => api.patch(`/leaves/${id}/cancel`),
  confirmResume: (id: string) => api.patch(`/leaves/${id}/resume`),
};

export const approvalApi = {
  getInbox: (params?: any) => api.get('/approvals/inbox', { params }),
  action: (stepId: string, body: { action: string; comment?: string }) => api.post(`/approvals/${stepId}/action`, body),
  bulk: (stepIds: string[], action: string, comment?: string) => api.post('/approvals/bulk', { stepIds, action, comment }),
delegate: (data: any) => api.post('/approvals/delegate', data),
  revokeDelegate: (id: string) => api.delete(`/approvals/delegate/${id}`),
  getTeamCalendar: (params?: any) => api.get('/approvals/team-calendar', { params }),
};

export const hierarchyApi = {
  getActive: () => api.get('/hierarchy/active'),
  getHistory: () => api.get('/hierarchy/history'),
  getPendingChanges: () => api.get('/hierarchy/pending-changes'),
  update: (data: any) => api.patch('/hierarchy', data),
  getApprovalPath: (userId: string, leaveTypeId: string) => api.get(`/hierarchy/approval-path/${userId}/${leaveTypeId}`),
};

export const leaveTypeApi = {
  getAll: () => api.get('/leave-types'),
  create: (data: any) => api.post('/leave-types', data),
  update: (id: string, data: any) => api.patch(`/leave-types/${id}`, data),
  remove: (id: string) => api.delete(`/leave-types/${id}`),
};

export const dashboardApi = {
  employee: () => api.get('/dashboard/employee'),
  md: () => api.get('/dashboard/md'),
  team: () => api.get('/dashboard/team'),
};

export const notificationApi = {
  getAll: (params?: any) => api.get('/notifications', { params }),
  markRead: (id: string) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/read-all'),
  clearAll: () => api.delete('/notifications/clear-all'),
};

export const auditApi = {
  getAll: (params?: any) => api.get('/audit', { params }),
};

export const reportApi = {
  leaveRegister: (params?: any) => api.get('/reports/leave-register', { params }),
  balanceSummary: (params?: any) => api.get('/reports/balance-summary', { params }),
};

export const settingsApi = {
  getAll: () => api.get('/settings'),
  update: (data: Record<string, string>) => api.patch('/settings', data),
};

export const holidayApi = {
  getAll: (year?: number) => api.get('/holidays', { params: { year } }),
  create: (data: any) => api.post('/holidays', data),
    remove: (id: string) => api.delete(`/holidays/${id}`),
  uploadCsv: (formData: FormData) => api.post('/holidays/upload-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  download: (year: number) => api.get('/holidays/download', { params: { year }, responseType: 'blob' }),
};

export const policyApi = {
  getDocuments: () => api.get('/policies/documents'),
  uploadDocument: (formData: FormData) => api.post('/policies/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteDocument: (id: string) => api.delete(`/policies/documents/${id}`),
};
